import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useFeeds } from '../../hooks/useFeeds';
import {
  createFeed, 
  deleteFeed, 
  validateFeedExists,
} from '../../api/feedsHttpClient';
import { FeedScope } from '../../api/models/Feed';
import ActionConfirmation from '../../../../Common/components/ActionConfirmation/ActionConfirmation';
import CreateFeedDialog from '../CreateFeedDialog/CreateFeedDialog';
import FeedsTable from '../FeedsTable/FeedsTable';

interface AzureFeedsProps {
  projectId: string;
  toolId: string;
  toolProjectId: string;
}

const AzureFeeds: React.FC<AzureFeedsProps> = ({ projectId, toolId, toolProjectId }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { feeds, loading, error, fetchFeeds } = useFeeds();

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newFeedName, setNewFeedName] = useState('');
  const [newFeedScope, setNewFeedScope] = useState<FeedScope>(FeedScope.Organisation);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [feedNameExists, setFeedNameExists] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    show: boolean;
    feedName: string;
    feedScope: FeedScope;
  }>({ show: false, feedName: '', feedScope: FeedScope.Organisation });

  useEffect(() => {
    fetchFeeds(projectId, toolId);
  }, [fetchFeeds, projectId, toolId, toolProjectId]);

  // Debounced validation
  const debouncedValidation = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (name: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          checkFeedNameExists(name);
        }, 500);
      };
    })(),
    [toolId, toolProjectId],
  );

  const checkFeedNameExists = async (name: string) => {
    if (!name.trim()) {
      setFeedNameExists(false);
      return;
    }
    setIsValidating(true);
    try {
      const exists = await validateFeedExists(toolId, toolProjectId, name.trim());
      setFeedNameExists(exists);
    } catch (err) {
      console.error('Error validating feed name:', err);
      setFeedNameExists(false);
    } finally {
      setIsValidating(false);
    }
  };

  const handleFeedNameChange = (name: string) => {
    setNewFeedName(name);
    if (createError) setCreateError(null);
    setFeedNameExists(false);
    if (/\s/.test(name)) {
      setCreateError('Feed name cannot contain whitespace');
      return;
    }
    debouncedValidation(name);
  };

  const handleCreateFeed = async () => {
    if (!newFeedName.trim()) {
      setCreateError('Feed name is required');
      return;
    }
    if (/\s/.test(newFeedName)) {
      setCreateError('Feed name cannot contain whitespace');
      return;
    }

    const feedNameToCreate = newFeedName.trim();

    try {
      const exists = await validateFeedExists(toolId, toolProjectId, feedNameToCreate);
      if (exists) {
        setCreateError('A feed with this name already exists');
        return;
      }
    } catch (err) {
      setCreateError('Error validating feed name');
      return;
    }

    setCreateError(null);
    setIsCreateDialogOpen(false);
    setNewFeedName('');
    setNewFeedScope(FeedScope.Organisation);
    setIsLoading(true);

    try {
      await createFeed({ projectId, toolId, toolProjectId, feedName: feedNameToCreate, scope: newFeedScope });
      enqueueSnackbar(`Azure Feed "${feedNameToCreate}" has been created successfully!`, {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      await fetchFeeds(projectId, toolId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      setCreateError(errorMessage);
      enqueueSnackbar(`Failed to create Azure Feed: ${errorMessage}`, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteFeed = (feedName: string) => {
    const feed = feeds.find((f) => f.feedName === feedName);
    setDeleteConfirmation({
      show: true,
      feedName,
      feedScope: feed?.feedScope ?? FeedScope.Project,
    });
  };

  const confirmDeleteFeed = async () => {
    const deletedFeedName = deleteConfirmation.feedName;
    const deletedFeedScope = deleteConfirmation.feedScope;
    setDeleteConfirmation({ show: false, feedName: '', feedScope: FeedScope.Organisation });
    setIsLoading(true);

    try {
      await deleteFeed({ projectId, toolId, toolProjectId, feedName: deletedFeedName, scope: deletedFeedScope });
      enqueueSnackbar(`Azure Feed "${deletedFeedName}" has been deleted successfully!`, {
        variant: 'success',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      await fetchFeeds(projectId, toolId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred';
      enqueueSnackbar(`Failed to delete Azure Feed: ${errorMessage}`, {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
      console.error('Failed to delete feed:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDeleteFeed = () => {
    setDeleteConfirmation({ show: false, feedName: '', feedScope: FeedScope.Organisation });
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewFeedName('');
    setNewFeedScope(FeedScope.Organisation);
    setCreateError(null);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: 'white',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}
      >
        <Typography variant="h6">Azure Feeds</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setIsCreateDialogOpen(true)}
          disabled={isLoading}
        >
          Create Feed
        </Button>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <FeedsTable
            azureFeeds={feeds}
            isLoading={isLoading}
            onDeleteFeed={handleDeleteFeed}
          />
        )}
      </Box>

      <CreateFeedDialog
        open={isCreateDialogOpen}
        feedName={newFeedName}
        feedScope={newFeedScope}
        createError={createError}
        isLoading={isLoading}
        isValidating={isValidating}
        feedNameExists={feedNameExists}
        onClose={handleCloseCreateDialog}
        onFeedNameChange={handleFeedNameChange}
        onFeedScopeChange={setNewFeedScope}
        onCreateFeed={handleCreateFeed}
      />

      <ActionConfirmation
        when={deleteConfirmation.show}
        title="Delete Azure Feed"
        description={`Are you sure you want to delete the feed "${deleteConfirmation.feedName}"? This action cannot be undone.`}
        confirm={confirmDeleteFeed}
        cancel={cancelDeleteFeed}
      />

      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default AzureFeeds;
